// Pure helpers for the CommentThread (PRD §9.5, B2 PR4 / B2d).
//
// Lives in its own file so CommentThread.tsx contains only the
// component export — keeps Vite's react-refresh happy and lets the
// grouping logic be unit-tested without rendering the component.

import type { components } from '../api/schema';

type CommentOut = components['schemas']['CommentOut'];

export interface FrameGroup {
  /** null = "General" bucket (no frame anchor). */
  frame: number | null;
  comments: CommentOut[];
}

/**
 * Group comments by frame anchor, preserving insertion order within
 * each bucket. The "General" bucket (frame === null) renders first,
 * then numeric frames ascending — matches how a reviewer scans a
 * shot top-to-bottom: context first, then per-frame notes in time
 * order.
 */
export function groupByFrame(comments: CommentOut[]): FrameGroup[] {
  const general: CommentOut[] = [];
  const buckets = new Map<number, CommentOut[]>();
  for (const c of comments) {
    if (c.frame === null) {
      general.push(c);
      continue;
    }
    const existing = buckets.get(c.frame);
    if (existing) {
      existing.push(c);
    } else {
      buckets.set(c.frame, [c]);
    }
  }
  const groups: FrameGroup[] = [];
  if (general.length > 0) groups.push({ frame: null, comments: general });
  const orderedFrames = [...buckets.keys()].sort((a, b) => a - b);
  for (const f of orderedFrames) {
    groups.push({ frame: f, comments: buckets.get(f) ?? [] });
  }
  return groups;
}
